'use client';

import { useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/app/impact-dashboard-homepage/components/Footer';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Lato:wght@300;400;700&display=swap');

  .rr-donate * { box-sizing: border-box; }

  .rr-donate {
    font-family: 'Lato', sans-serif;
    background: #FBF7EE;
    color: #2A1200;
    overflow-x: hidden;
    min-height: 100vh;
  }

  /* HERO */
  .rr-hero {
    position: relative;
    min-height: 52vh;
    display: flex;
    align-items: flex-end;
    padding: 68px 0 4rem 0;
    background: linear-gradient(155deg, #7A2E06 0%, #9B4215 50%, #B05520 100%);
    overflow: hidden;
  }
  .rr-hero-bg {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 70% 55% at 72% 30%, rgba(196,154,26,0.13) 0%, transparent 65%),
      radial-gradient(ellipse 45% 65% at 12% 85%, rgba(251,247,238,0.07) 0%, transparent 55%);
  }
  .rr-hero-content {
    position: relative;
    z-index: 2;
    padding: 0 3.5rem;
    animation: rrFadeUp 0.9s ease both;
  }
  .rr-hero-eyebrow {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #E8C96A;
    background: rgba(232,201,106,0.14);
    border: 1px solid rgba(232,201,106,0.38);
    padding: 0.3rem 0.9rem;
    border-radius: 2px;
    margin-bottom: 1.2rem;
  }
  .rr-hero h1 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(2.4rem, 5vw, 3.8rem);
    font-weight: 900;
    color: #FBF7EE;
    line-height: 1.1;
    margin-bottom: 1.1rem;
  }
  .rr-hero h1 em { color: #EDD28A; font-style: normal; }
  .rr-hero-divider {
    width: 55px; height: 3px;
    background: linear-gradient(90deg, #C49A1A, transparent);
    margin: 1.2rem 0;
  }
  .rr-hero p {
    font-size: 1.03rem;
    color: rgba(251,247,238,0.70);
    max-width: 500px;
    line-height: 1.8;
    font-weight: 300;
  }

  /* MAIN */
  .rr-main {
    max-width: 1040px;
    margin: 0 auto;
    padding: 4rem 2rem 6rem;
  }

  /* DISCLAIMER */
  .rr-disclaimer {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    background: #F4EDD8;
    border-left: 4px solid #8B3A0F;
    border-radius: 0 10px 10px 0;
    padding: 1.3rem 1.6rem;
    margin-bottom: 3.2rem;
    box-shadow: 0 2px 10px rgba(139,58,15,0.06);
    animation: rrFadeUp 0.7s 0.2s ease both;
    opacity: 0;
    animation-fill-mode: forwards;
  }
  .rr-disclaimer-icon { font-size: 1.4rem; flex-shrink: 0; margin-top: 0.1rem; }
  .rr-disclaimer-text { font-size: 0.92rem; line-height: 1.75; color: #3D1800; }
  .rr-disclaimer-text strong { color: #8B3A0F; font-weight: 700; }

  /* DONATE CARD */
  .rr-donate-card {
    background: #8B3A0F;
    border-radius: 16px;
    padding: 2.8rem 3rem;
    margin-bottom: 3.2rem;
    position: relative;
    overflow: hidden;
    box-shadow: 0 10px 36px rgba(139,58,15,0.18);
    animation: rrFadeUp 0.7s 0.35s ease both;
    opacity: 0;
    animation-fill-mode: forwards;
  }
  .rr-donate-card::before {
    content: '';
    position: absolute;
    top: -50px; right: -50px;
    width: 220px; height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(196,154,26,0.14) 0%, transparent 70%);
    pointer-events: none;
  }
  .rr-card-label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #EDD28A;
    margin-bottom: 0.7rem;
  }
  .rr-donate-card h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.6rem;
    color: #FBF7EE;
    margin-bottom: 0.9rem;
    font-weight: 700;
  }
  .rr-donate-card > p {
    color: rgba(251,247,238,0.65);
    font-size: 0.95rem;
    line-height: 1.75;
    max-width: 520px;
    margin-bottom: 2rem;
    font-weight: 300;
  }
  .rr-donate-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    background: #C49A1A;
    color: #1E0800;
    font-family: 'Lato', sans-serif;
    font-size: 0.92rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-decoration: none;
    padding: 0.95rem 2.2rem;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(196,154,26,0.38);
    transition: transform 0.18s, box-shadow 0.18s, background 0.18s;
  }
  .rr-donate-btn:hover {
    background: #D4AA28;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(196,154,26,0.48);
  }
  .rr-url-display {
    margin-top: 1.5rem;
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    background: rgba(251,247,238,0.08);
    border: 1px solid rgba(251,247,238,0.16);
    border-radius: 6px;
    padding: 0.65rem 1rem;
  }
  .rr-url-display a {
    font-family: 'Courier New', monospace;
    font-size: 0.8rem;
    color: #EDD28A;
    text-decoration: none;
  }
  .rr-url-display a:hover { text-decoration: underline; }

  /* SECTION HEADER */
  .rr-section-header {
    text-align: center;
    margin-bottom: 2.4rem;
  }
  .rr-section-header h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1.95rem;
    font-weight: 700;
    color: #2A1200;
    margin-bottom: 0.5rem;
  }
  .rr-section-header p { color: #7A4E28; font-size: 0.98rem; font-weight: 300; }
  .rr-divider-line {
    width: 48px; height: 3px;
    background: #C49A1A;
    margin: 0.9rem auto 0;
    border-radius: 2px;
  }

  /* STEPS */
  .rr-steps { display: grid; gap: 1.3rem; }
  .rr-step {
    display: grid;
    grid-template-columns: 68px 1fr;
    gap: 1.4rem;
    align-items: start;
    background: #F4EDD8;
    border: 1px solid rgba(139,58,15,0.09);
    border-radius: 14px;
    padding: 1.9rem 2.1rem;
    box-shadow: 0 3px 14px rgba(139,58,15,0.05);
    transition: box-shadow 0.2s, transform 0.2s;
    opacity: 0;
    animation: rrFadeUp 0.6s ease both;
    animation-fill-mode: forwards;
  }
  .rr-step:nth-child(1) { animation-delay: 0.45s; }
  .rr-step:nth-child(2) { animation-delay: 0.55s; }
  .rr-step:nth-child(3) { animation-delay: 0.65s; }
  .rr-step:hover {
    box-shadow: 0 7px 24px rgba(139,58,15,0.10);
    transform: translateY(-2px);
  }
  .rr-step-num {
    width: 50px; height: 50px;
    border-radius: 50%;
    background: #8B3A0F;
    color: #EDD28A;
    font-family: 'Playfair Display', serif;
    font-size: 1.3rem;
    font-weight: 900;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(139,58,15,0.25);
  }
  .rr-step-body h4 {
    font-family: 'Playfair Display', serif;
    font-size: 1.1rem;
    font-weight: 700;
    color: #2A1200;
    margin-bottom: 0.5rem;
  }
  .rr-step-body p { font-size: 0.92rem; color: #5C3010; line-height: 1.78; margin-bottom: 0.4rem; }
  .rr-step-body p strong { color: #8B3A0F; font-weight: 700; }
  .rr-step-body a {
    color: #8B3A0F;
    font-weight: 700;
    text-decoration: none;
    border-bottom: 1px dashed #C49A1A;
  }
  .rr-step-body a:hover { color: #5C1E04; }
  .rr-step-note {
    margin-top: 0.6rem;
    font-style: italic;
    font-size: 0.87rem !important;
    color: #7A5030 !important;
  }
  .rr-highlight {
    margin-top: 0.9rem;
    margin-bottom: 0.9rem;
    background: rgba(139,58,15,0.05);
    border: 1px solid rgba(139,58,15,0.15);
    border-radius: 8px;
    padding: 0.82rem 1rem;
    font-size: 0.9rem;
    color: #8B3A0F;
    font-weight: 700;
  }
  .rr-highlight code {
    font-family: 'Courier New', monospace;
    background: rgba(139,58,15,0.08);
    padding: 0.1em 0.45em;
    border-radius: 3px;
    color: #5C1E04;
  }

  /* IMPACT */
  .rr-impact {
    margin-top: 3.8rem;
    text-align: center;
    padding: 2.8rem 2rem;
    background: linear-gradient(135deg, #7A2E06, #9B4215);
    border-radius: 18px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(139,58,15,0.18);
  }
  .rr-impact::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 50% 0%, rgba(196,154,26,0.11) 0%, transparent 62%);
    pointer-events: none;
  }
  .rr-impact h3 {
    position: relative;
    font-family: 'Playfair Display', serif;
    font-size: 1.75rem;
    color: #FBF7EE;
    margin-bottom: 0.7rem;
  }
  .rr-impact-divider {
    width: 38px; height: 2px;
    background: #C49A1A;
    margin: 0.7rem auto 0.9rem;
  }
  .rr-impact p {
    position: relative;
    color: rgba(251,247,238,0.62);
    font-size: 0.94rem;
    max-width: 540px;
    margin: 0 auto;
    line-height: 1.8;
    font-weight: 300;
  }

  /* ANIMATION */
  @keyframes rrFadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* RESPONSIVE */
  @media (max-width: 640px) {
    .rr-hero-content { padding: 0 1.5rem; }
    .rr-donate-card { padding: 1.8rem 1.5rem; }
    .rr-main { padding: 2.5rem 1rem 4rem; }
    .rr-step { grid-template-columns: 1fr; }
  }
`;

export default function DonationsPage() {
  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = styles;
    document.head?.appendChild(styleTag);
    return () => {
      document.head?.removeChild(styleTag);
    };
  }, []);

  return (
    <div className="rr-donate">
      <Header />

      {/* HERO */}
      <section className="rr-hero" style={{ paddingTop: '84px' }}>
        <div className="rr-hero-bg" />
        <div className="rr-hero-content">
          <div className="rr-hero-eyebrow">Support the Mission</div>
          <h1>Help Us Protect <em>Rhinos &amp; Rangers</em></h1>
          <div className="rr-hero-divider" />
          <p>
            Every donation fuels frontline conservation work — from anti-poaching patrols to habitat preservation.
            Your generosity keeps rhinos alive for the next generation.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <main className="rr-main">

        {/* DISCLAIMER */}
        <div className="rr-disclaimer">
          <div className="rr-disclaimer-icon">⚠️</div>
          <div className="rr-disclaimer-text">
            <strong>Important Note:</strong> Rhinos&amp;Rangers is a conservation awareness initiative, not a
            registered business or non-profit, and therefore <strong>does not directly receive donations</strong>. We partner
            with <strong>Earth Ltd.</strong> to channel all donated funds toward rhino conservation efforts. When donating, you
            will be directed to Earth Ltd.&apos;s secure donations page. Simply indicate <em>&ldquo;In Honor of: Rhinos&amp;Rangers&rdquo;</em>{' '}
            so your contribution is attributed to our cause.
          </div>
        </div>

        {/* DONATE LINK CARD */}
        <div className="rr-donate-card">
          <div className="rr-card-label">Official Donation Portal</div>
          <h3>Donate via Earth Ltd.</h3>
          <p>
            Click the button below to be taken to Earth Ltd.&apos;s secure donation page. All contributions made in
            honor of Rhinos&amp;Rangers go directly toward rhino conservation programs.
          </p>
          <a
            className="rr-donate-btn"
            href="https://www.earthltd.org/donate/"
            target="_blank"
            rel="noopener noreferrer"
          >
            ❤️ Donate Now — Earth Ltd. Portal
          </a>
          <div className="rr-url-display">
            <span>🔗</span>
            <a
              href="https://www.earthltd.org/donate/"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.earthltd.org/donate/
            </a>
          </div>
        </div>

        {/* STEPS HEADER */}
        <div className="rr-section-header">
          <h2>How to Donate</h2>
          <p>Follow these three simple steps to complete your donation</p>
          <div className="rr-divider-line" />
        </div>

        {/* STEPS */}
        <div className="rr-steps">

          {/* Step 1 */}
          <div className="rr-step">
            <div className="rr-step-num">1</div>
            <div className="rr-step-body">
              <h4>Visit the Earth Ltd. Donations Page</h4>
              <p>
                Click the <strong>&ldquo;Donate Now&rdquo;</strong> button above or visit{' '}
                <a
                  href="https://www.earthltd.org/donate/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.earthltd.org/donate/
                </a>{' '}
                directly. This will take you to Earth Ltd.&apos;s secure donation portal.
              </p>
              <p className="rr-step-note">
                Note: Rhinos&amp;Rangers does not receive donations directly, as it is not a registered business or non-profit.
                Earth Ltd. is our trusted partner that processes and directs all funds toward rhino conservation work on our behalf.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="rr-step">
            <div className="rr-step-num">2</div>
            <div className="rr-step-body">
              <h4>Select &ldquo;In Honor of&rdquo; &amp; Enter Rhinos&amp;Rangers</h4>
              <p>
                On the Earth Ltd. donations page, find the <strong>&ldquo;I&apos;d like to give to&rdquo;</strong> section.
                Tap the <strong>&ldquo;In Honor of:&rdquo;</strong> button, then in the moments/name box that appears, type:
              </p>
              <div className="rr-highlight">
                In Honor of: <code>Rhinos&amp;Rangers</code>
              </div>
              <p>This ensures your donation is credited to the Rhinos&amp;Rangers conservation initiative.</p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="rr-step">
            <div className="rr-step-num">3</div>
            <div className="rr-step-body">
              <h4>Complete Your Donation Details</h4>
              <p>
                Fill in the remaining required fields — your <strong>donation amount</strong>,{' '}
                <strong>personal information</strong>, and <strong>payment details</strong> — then submit the form.
                You will receive a confirmation once your donation has been processed. Thank you for standing with the rhinos!
              </p>
            </div>
          </div>

        </div>

        {/* IMPACT */}
        <div className="rr-impact">
          <h3>Every Dollar Counts</h3>
          <div className="rr-impact-divider" />
          <p>
            Through our partnership with Earth Ltd., donations raised in honor of Rhinos&amp;Rangers support
            boots-on-the-ground conservation — anti-poaching units, ranger training, and protected habitat programs that give
            rhinos a fighting chance.
          </p>
        </div>

      </main>

      <Footer />
    </div>
  );
}
